import { useState, useEffect, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSocket } from '../socket/socketClient'
import { notificationsAPI } from '../api/notifications'
import type { INotification } from '../types/notification'

export const useNotifications = () => {
  const queryClient = useQueryClient()
  const [liveNotif, setLiveNotif] = useState<INotification | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsAPI.getAll({ limit: 20 }),
    staleTime: 30_000,
  })

  useEffect(() => {
    const socket = getSocket()
    if (!socket) return

    const handleNewNotif = (notif: INotification) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      setLiveNotif(notif)
    }

    socket.on('notification:new', handleNewNotif)
    return () => { socket.off('notification:new', handleNewNotif) }
  }, [queryClient])

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationsAPI.markRead(id)
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    } catch {
      // silent
    }
  }, [queryClient])

  const markAllRead = useCallback(async () => {
    try {
      await notificationsAPI.markAllRead()
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    } catch {
      // silent
    }
  }, [queryClient])

  return {
    notifications: data?.notifications ?? [],
    unreadCount: data?.unreadCount ?? 0,
    isLoading,
    liveNotif,
    markAsRead,
    markAllRead,
  }
}
